import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, ChevronDown, Monitor, Shield, Cloud, Wifi, Lightbulb, ClipboardCheck, Headphones, BookOpen, Calculator, FileText, BarChart3, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import NavbarMobileMenu from '@/components/NavbarMobileMenu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { settings } = useSiteSettings();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => {
      setRecentBlogs((res.data || []).slice(0, 3));
    }).catch(() => {});
  }, []);

  const megaMenuItems = [
    { icon: Monitor, title: 'Managed IT Services', desc: 'Complete 24/7 infrastructure management, proactive monitoring, and help desk support.', href: '/managed-it' },
    { icon: Shield, title: 'Cybersecurity', desc: 'Multi-layered EDR, SIEM, dark web monitoring, and incident response.', href: '/cybersecurity' },
    { icon: Cloud, title: 'Microsoft 365 Support', desc: 'M365 migration, security hardening, SharePoint, Teams, and Defender setup.', href: '/microsoft-365-support' },
    { icon: Wifi, title: 'Network Infrastructure', desc: 'Enterprise-grade network design, WiFi, firewall management, and SD-WAN.', href: '/network' },
    { icon: Lightbulb, title: 'IT Strategy & Consulting', desc: 'Virtual CIO services, technology roadmaps, and budget planning.', href: '/it-strategy' },
    { icon: ClipboardCheck, title: 'Compliance', desc: 'HIPAA, SOC 2, PCI DSS, PIPEDA compliance support and audit preparation.', href: '/compliance' },
    { icon: Headphones, title: 'Help Desk Support', desc: '24/7 remote support with under 15 minute response time.', href: '/help-desk' },
  ];

  const services = megaMenuItems.map(s => ({ name: s.title, href: s.href }));

  const scrollTo = (hash) => {
    setIsMobileOpen(false);
    const go = (smooth) => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    };
    if (isHomePage) {
      go(true);
      setTimeout(() => go(false), 1000);
      setTimeout(() => go(true), 2000);
    } else {
      navigate('/');
      setTimeout(() => go(false), 800);
      setTimeout(() => go(false), 1800);
      setTimeout(() => go(true), 2800);
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass' : 'bg-transparent'
        }`}
        data-testid="navbar"
        data-dark
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo">
              <img 
                src={settings.logo_url || '/clickonemsp-logo.png'} 
                alt={settings.company_name || 'Click One MSP'} 
                className="h-[120px] w-auto object-contain"
                width="120"
                height="120"
                onError={(e) => { e.target.src = '/clickonemsp-logo.png'; }}
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {/* Home Link */}
              <Link
                to="/"
                className="nav-link text-sm text-gray-400 hover:text-white transition-colors font-medium"
                data-testid="nav-home"
              >
                Home
              </Link>

              {/* Services Mega Menu Trigger */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  className="nav-link flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors font-medium"
                  data-testid="nav-services"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {/* Invisible bridge to mega menu panel */}
                {servicesOpen && <div className="absolute top-full left-0 w-full h-8" />}
              </div>

              <button
                onClick={() => scrollTo('#calculator')}
                className="nav-link text-sm text-gray-400 hover:text-white transition-colors font-medium"
                data-testid="nav-calculator"
              >
                Pricing
              </button>

              {/* Resources Mega Menu Trigger */}
              <div 
                className="relative"
                onMouseEnter={() => setResourcesOpen(true)}
                onMouseLeave={() => setResourcesOpen(false)}
              >
                <button
                  className="nav-link flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors font-medium"
                  data-testid="nav-resources"
                >
                  Resources
                  <ChevronDown className={`w-4 h-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
                </button>
                {resourcesOpen && <div className="absolute top-full left-0 w-full h-8" />}
              </div>

              <Link
                to="/contact-us"
                className="nav-link text-sm text-gray-400 hover:text-white transition-colors font-medium"
                data-testid="nav-contact"
              >
                Contact
              </Link>
            </div>

            {/* CTA */}
            <div className="hidden lg:block">
              <Button
                onClick={() => {
                  if (location.pathname === '/contact-us') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    navigate('/contact-us');
                  }
                }}
                className="btn-primary-new px-6 py-5 rounded-none font-medium"
                data-testid="nav-cta"
              >
                Get Protected
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mobile Toggle */}
            <button
              className="lg:hidden text-white"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Desktop Mega Menu Panel */}
      <div
        className={`fixed top-20 left-0 right-0 z-40 hidden lg:block transition-all duration-200 ${servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'}`}
        onMouseEnter={() => setServicesOpen(true)}
        onMouseLeave={() => setServicesOpen(false)}
        data-testid="mega-menu"
      >
        <div className="bg-[#0A0A0A]/98 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-black/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
            <div className="flex items-center justify-between mb-8">
              <Link to="/industries" className="text-[#E63946] text-sm font-semibold flex items-center gap-1.5 hover:text-red-400 transition-colors">
                All Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-4 gap-x-8 gap-y-8">
              {megaMenuItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setServicesOpen(false)}
                  className="group flex gap-4 items-start"
                  data-testid={`mega-${item.href.slice(1)}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 border border-white/10 group-hover:border-[#E63946]/50 flex items-center justify-center transition-colors">
                    <item.icon className="w-5 h-5 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold group-hover:text-[#E63946] transition-colors">{item.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed mt-1">{item.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Resources Mega Menu Panel */}
      <div
        className={`fixed top-20 left-0 right-0 z-40 hidden lg:block transition-all duration-200 ${resourcesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'}`}
        onMouseEnter={() => setResourcesOpen(true)}
        onMouseLeave={() => setResourcesOpen(false)}
        data-testid="mega-resources"
      >
        <div className="bg-[#0A0A0A]/98 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-black/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
            <div className="grid grid-cols-12 gap-8">
              {/* Left - Resource Links */}
              <div className="col-span-4 space-y-6">
                <Link to="/blog" onClick={() => setResourcesOpen(false)} className="group flex gap-4 items-start" data-testid="mega-blog">
                  <div className="flex-shrink-0 w-10 h-10 border border-white/10 group-hover:border-[#E63946]/50 flex items-center justify-center transition-colors">
                    <FileText className="w-5 h-5 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold group-hover:text-[#E63946] transition-colors">Blog</h4>
                    <p className="text-gray-500 text-xs leading-relaxed mt-1">IT tips, cybersecurity insights, and technology guides.</p>
                  </div>
                </Link>
                <Link to="/case-studies" onClick={() => setResourcesOpen(false)} className="group flex gap-4 items-start" data-testid="mega-case-studies">
                  <div className="flex-shrink-0 w-10 h-10 border border-white/10 group-hover:border-[#E63946]/50 flex items-center justify-center transition-colors">
                    <BarChart3 className="w-5 h-5 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold group-hover:text-[#E63946] transition-colors">Case Studies</h4>
                    <p className="text-gray-500 text-xs leading-relaxed mt-1">Real client success stories and measurable results.</p>
                  </div>
                </Link>
                <Link to="/roi-calculator" onClick={() => setResourcesOpen(false)} className="group flex gap-4 items-start" data-testid="mega-roi">
                  <div className="flex-shrink-0 w-10 h-10 border border-white/10 group-hover:border-[#E63946]/50 flex items-center justify-center transition-colors">
                    <Calculator className="w-5 h-5 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold group-hover:text-[#E63946] transition-colors">ROI Calculator</h4>
                    <p className="text-gray-500 text-xs leading-relaxed mt-1">Calculate how much IT downtime is costing your business.</p>
                  </div>
                </Link>
                <Link to="/cybersecurity-assessment" onClick={() => setResourcesOpen(false)} className="group flex gap-4 items-start" data-testid="mega-assessment">
                  <div className="flex-shrink-0 w-10 h-10 border border-white/10 group-hover:border-[#E63946]/50 flex items-center justify-center transition-colors">
                    <ShieldCheck className="w-5 h-5 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold group-hover:text-[#E63946] transition-colors flex items-center gap-2">
                      Cybersecurity Assessment
                      <span className="text-[9px] px-1.5 py-0.5 bg-[#E63946] text-white font-bold tracking-wider">FREE</span>
                    </h4>
                    <p className="text-gray-500 text-xs leading-relaxed mt-1">Score your security in 2 minutes. Get top 3 gaps & a fix plan.</p>
                  </div>
                </Link>
                <Link to="/industries" onClick={() => setResourcesOpen(false)} className="group flex gap-4 items-start" data-testid="mega-industries">
                  <div className="flex-shrink-0 w-10 h-10 border border-white/10 group-hover:border-[#E63946]/50 flex items-center justify-center transition-colors">
                    <BookOpen className="w-5 h-5 text-[#E63946]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold group-hover:text-[#E63946] transition-colors">Industries We Serve</h4>
                    <p className="text-gray-500 text-xs leading-relaxed mt-1">Healthcare, legal, construction, real estate, and more.</p>
                  </div>
                </Link>
              </div>

              {/* Right - Tech Talks (Recent Blogs) */}
              <div className="col-span-8 border-l border-white/10 pl-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white text-sm font-semibold uppercase tracking-wider">Tech Talks</h3>
                  <Link to="/blog" onClick={() => setResourcesOpen(false)} className="text-[#E63946] text-xs font-medium flex items-center gap-1 hover:text-red-400 transition-colors">
                    View All <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-3 gap-6">
                  {recentBlogs.map((blog) => (
                    <Link
                      key={blog.slug}
                      to={`/post/${blog.slug}`}
                      onClick={() => setResourcesOpen(false)}
                      className="group"
                    >
                      {blog.image_url && (
                        <div className="aspect-video bg-white/5 border border-white/10 overflow-hidden mb-3">
                          <img src={blog.image_url} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                      )}
                      <h4 className="text-white text-xs font-medium line-clamp-2 group-hover:text-[#E63946] transition-colors leading-relaxed">{blog.title}</h4>
                      <p className="text-gray-600 text-[10px] mt-1">{blog.category || 'Technology'}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <NavbarMobileMenu
        isOpen={isMobileOpen}
        onClose={() => setIsMobileOpen(false)}
        services={services}
        scrollToHash={scrollTo}
        onContactClick={() => {
          setIsMobileOpen(false);
          if (location.pathname === '/contact-us') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigate('/contact-us');
          }
        }}
      />
    </>
  );
};

export default Navbar;
